import { useParams, Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { comics, categories } from '@/mocks/comics';
import { readingPages, defaultReadingPages } from '@/mocks/readingPages';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [selectedChapter, setSelectedChapter] = useState(1);
  const [showAllChapters, setShowAllChapters] = useState(false);

  const comicId = Number(id);
  const comic = useMemo(() => comics.find((c) => c.id === comicId), [comicId]);

  const category = useMemo(() => {
    if (!comic) return null;
    return categories.find((c) => c.slug === comic.category);
  }, [comic]);

  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter((c) => c.category === comic.category && c.id !== comic.id)
      .slice(0, 4);
  }, [comic]);

  const currentPages = useMemo(() => {
    if (!comic) return defaultReadingPages;
    const comicPages = readingPages[comic.id];
    if (comicPages && comicPages[selectedChapter]) {
      return comicPages[selectedChapter];
    }
    return defaultReadingPages;
  }, [comic, selectedChapter]);

  if (!comic) {
    return (
      <main className="px-4 md:px-6 py-20 text-center">
        <p className="text-foreground-600">漫画不存在</p>
        <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block whitespace-nowrap cursor-pointer">返回首页</Link>
      </main>
    );
  }

  const visibleChapters = showAllChapters
    ? Array.from({ length: comic.chapters }, (_, i) => comic.chapters - i)
    : Array.from({ length: Math.min(20, comic.chapters) }, (_, i) => comic.chapters - i);

  const handleChapterClick = (chapterNum: number) => {
    setSelectedChapter(chapterNum);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <main>
      <article className="px-4 md:px-6 py-8 md:py-12">
        <div className="max-w-[1280px] mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            {category && (
              <>
                <Link to={`/category/${category.slug}`} className="hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                  {category.name}
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
              </>
            )}
            <span className="text-foreground-800 font-medium">{comic.title}</span>
          </nav>

          <div className="flex flex-col md:flex-row gap-6 md:gap-8 mb-10">
            <div className="md:w-[280px] flex-shrink-0">
              <div className="aspect-[3/4] rounded-xl overflow-hidden">
                <img
                  src={comic.cover}
                  alt={`${comic.title} 漫画封面`}
                  title={`${comic.title} - 女神漫画网高清免费阅读下拉式`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{comic.title}</h1>

              <div className="flex flex-wrap items-center gap-3 mt-3">
                <span className="text-sm text-foreground-600">作者：<strong>{comic.author}</strong></span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  comic.status === '连载中'
                    ? 'bg-accent-100 text-accent-700'
                    : 'bg-foreground-200 text-foreground-700'
                }`}>
                  {comic.status}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-4 mt-3">
                <div className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-400"></i>
                  <span className="text-sm font-semibold text-foreground-800">{comic.rating}</span>
                  <span className="text-xs text-foreground-500">分</span>
                </div>
                <span className="text-sm text-foreground-600">{Math.floor(comic.views / 10000)}万次阅读</span>
                <span className="text-sm text-foreground-600">共 {comic.chapters} 话</span>
              </div>

              <div className="flex flex-wrap gap-2 mt-3">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed mt-4">
                {comic.description}
              </p>

              <div className="flex items-center gap-3 mt-6">
                <button
                  onClick={() => handleChapterClick(1)}
                  className="px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  从第一话开始阅读
                </button>
                <button
                  onClick={() => handleChapterClick(comic.chapters)}
                  className="px-6 py-3 rounded-full bg-background-100 border border-background-300 text-foreground-700 text-sm font-medium hover:bg-primary-50 hover:border-primary-300 hover:text-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  阅读最新话
                </button>
              </div>
            </div>
          </div>

          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">章节目录</h2>
            <div className="bg-background-100 rounded-xl border border-background-200/70 p-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {visibleChapters.map((num) => (
                  <button
                    key={num}
                    onClick={() => handleChapterClick(num)}
                    className={`px-3 py-2 rounded-lg text-sm text-left transition-all cursor-pointer whitespace-nowrap ${
                      selectedChapter === num
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-700 hover:bg-background-200'
                    }`}
                  >
                    第{num}话
                  </button>
                ))}
              </div>
              {comic.chapters > 20 && (
                <button
                  onClick={() => setShowAllChapters(!showAllChapters)}
                  className="mt-4 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  {showAllChapters ? '收起' : `展开全部 ${comic.chapters} 话`}
                </button>
              )}
            </div>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
              阅读内容 - 第{selectedChapter}话
            </h2>
            <div className="bg-background-100 rounded-xl border border-background-200/70 p-4 md:p-6">
              <div className="max-w-[800px] mx-auto">
                <div className="space-y-4">
                  {currentPages.map((pageUrl, i) => (
                    <div key={i} className="bg-background-200 rounded-lg overflow-hidden">
                      <img
                        src={pageUrl}
                        alt={`${comic.title} 第${selectedChapter}话 第${i + 1}页`}
                        title={`${comic.title} 第${selectedChapter}话 第${i + 1}页 - 女神漫画网`}
                        className="w-full h-auto"
                      />
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between mt-8 pt-6 border-t border-background-200/70">
                  <button
                    onClick={() => selectedChapter > 1 && handleChapterClick(selectedChapter - 1)}
                    disabled={selectedChapter <= 1}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1 whitespace-nowrap cursor-pointer ${
                      selectedChapter <= 1
                        ? 'bg-background-200 text-foreground-400 cursor-not-allowed'
                        : 'bg-background-200 text-foreground-700 hover:bg-primary-50 hover:text-primary-600'
                    }`}
                  >
                    <i className="ri-arrow-left-line"></i> 上一话
                  </button>

                  <span className="text-sm text-foreground-500">第 {selectedChapter} / {comic.chapters} 话</span>

                  <button
                    onClick={() => selectedChapter < comic.chapters && handleChapterClick(selectedChapter + 1)}
                    disabled={selectedChapter >= comic.chapters}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1 whitespace-nowrap cursor-pointer ${
                      selectedChapter >= comic.chapters
                        ? 'bg-background-200 text-foreground-400 cursor-not-allowed'
                        : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                    }`}
                  >
                    下一话 <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </section>

          {relatedComics.length > 0 && (
            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-product-shop="">
                {relatedComics.map((r) => (
                  <Link
                    key={r.id}
                    to={`/article/${r.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <img
                        src={r.cover}
                        alt={`${r.title} 漫画封面`}
                        title={`${r.title} - 女神漫画网免费阅读`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 truncate">{r.title}</h3>
                      <div className="flex items-center gap-0.5 mt-1">
                        <i className="ri-star-fill text-accent-400 text-xs"></i>
                        <span className="text-xs text-foreground-600">{r.rating}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>
    </main>
  );
}